import React, { useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import Button from './Button';

interface Section {
  id: string;
  title: string;
}

interface FormProps {
  sections: Section[];
  onSave: () => void;
  onCancel?: () => void;
  children: React.ReactNode;
  isSubmitting?: boolean;
  hideSectionNav?: boolean;
  formTitle?: string;
  hideActions?: boolean;
}

const Form: React.FC<FormProps> = ({
  sections,
  onSave,
  onCancel,
  children,
  isSubmitting = false,
  hideSectionNav = false,
  formTitle,
  hideActions = false,
}) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);

    if (element) {
      // Get the main scrollable container from AdminLayout
      const mainContainer = document.querySelector('[data-main-container]');
      if (mainContainer) {
        const headerOffset = 200; // Increased to account for both nav bars
        const elementTop = element.offsetTop;

        mainContainer.scrollTo({
          top: elementTop - headerOffset,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <div className="flex flex-col">
      {/* Top Navigation Bar - Made sticky and flush */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-primary dark:border-gray-700 z-[1]">
        <div className="flex justify-end md:justify-between items-center px-4 py-2">
          {/* Section Navigation or Title */}
          {!hideSectionNav ? (
            <div className="hidden md:flex space-x-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                            hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer 
                            hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-md focus:outline-none"
                >
                  {section.title}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {formTitle || 'Form'}
              </h2>
            </div>
          )}

          {/* Action Buttons */}
          {!hideActions && (
            <div className="flex space-x-2">
              {onCancel && (
                <Button variant="secondary" onClick={onCancel} icon={ArrowLeft}>
                  Back
                </Button>
              )}
              <Button
                onClick={onSave}
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-6">
            {children}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form;
